import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  skipToken,
  hashKey,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { api, ApiResponse } from "_/utils";
import { Uuid } from "_/types";

import { PERMISSIONS_KEYS, permissions, Permission } from "_/data/rbac";
import { type User, useCheckUser } from "_/data/users";

const SCOPE = "orgs";
export const KEYS = {
  all: () => [SCOPE],
  org: (id: Uuid | undefined) => [SCOPE, "detail", id],
  orgUsers: (id: Uuid) => [...KEYS.org(id), "users"],
  current: () => [SCOPE, "current"],
};

export type Org = {
  /**
   * Organization ID.
   */
  id: Uuid;

  /**
   * Organizational domain ID.
   */
  domainId: Uuid;

  /**
   * Organization canonical name.
   */
  name: string;

  /**
   * Organization avatar blob ID.
   */
  avatarId?: string;

  /**
   * Whether machines in this organization run the internal operator UI and
   * expose unreleased features. Set by AON3D staff; off for manufacturing and
   * customer organizations.
   */
  internal: boolean;
};

/**
 * Struct for members of an organization.
 */
export type OrganizationUser = {
  /**
   * Organization the membership belongs to.
   */
  organizationId: string;

  /**
   * User within an organization.
   */
  user: User;

  /**
   * Flag to indicate whether the user has been approved as a member.
   */
  active: boolean;
};

export type OrganizationWithRbac = Org & {
  roles: string[];
  permissions: Permission[];
  isMember: boolean;
};

export type OrgUpdate = Partial<Pick<Org, "name" | "internal">>;

/**
 * Get the details of a specific organization by ID.
 */
async function getOrg(params: { id: Uuid }): Promise<OrganizationWithRbac> {
  const response = await api.get<ApiResponse<OrganizationWithRbac>>(
    `/organizations/${params.id}`
  );
  return response.data.data;
}

/**
 * Query hook for [[`getOrg`]].
 */
export function useOrg(
  id: Uuid | undefined,
  options: Omit<
    UseQueryOptions<OrganizationWithRbac>,
    "queryKey" | "queryFn"
  > = {}
) {
  return useQuery({
    queryKey: KEYS.org(id),
    queryFn: id ? () => getOrg({ id }) : skipToken,
    staleTime: 5 * 1000, // avoid dupe requests from the same page
    ...options,
  });
}

/**
 * Get listing of all orgs available to the current user.
 */
export async function getOrgs(): Promise<OrganizationWithRbac[]> {
  const response =
    await api.get<ApiResponse<OrganizationWithRbac[]>>(`/organizations`);
  return response.data.data;
}

/**
 * Query hook for [[`getOrgs`]].
 *
 * This hook is for the initial retrieval of orgs, allowing for the `useOrgs`
 * hook to be used for subsequent queries with a guaranteed defined value.
 */
export const useOrgs = (filter?: (org: OrganizationWithRbac) => boolean) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: KEYS.all(),
    queryFn: async () => {
      const orgs = await getOrgs();
      orgs.forEach((orgWithRbac) => {
        queryClient.setQueryData(KEYS.org(orgWithRbac.id), orgWithRbac);
        queryClient.setQueryData(
          PERMISSIONS_KEYS.currentUser(orgWithRbac.domainId),
          orgWithRbac.permissions
        );
      });
      return orgs;
    },
    select: filter ? (orgs) => orgs.filter(filter) : undefined,
  });
};

export function useAon3dOrgs(filter?: (org: OrganizationWithRbac) => boolean) {
  // Filtering orgs by name is a temporary solution until we can have a more
  // durable flag to mark internal orgs. `Org.internal` now exists, but it
  // means "machines show the internal UI", which excludes Manufacturing, so it
  // isn't a drop-in replacement for this grouping yet.
  const combinedFilter = (org: OrganizationWithRbac) => {
    return (
      org.name.toUpperCase().startsWith("AON3D") && (!filter || filter(org))
    );
  };
  return useOrgs(combinedFilter);
}

export function useFactoryOrg() {
  // Finding the factory org by name isn't the best way to do this long-term.
  // Long-term we should probably have a flag or org permission or something
  // that indicates that an org is a factory org.
  return useQuery<OrganizationWithRbac[], unknown, OrganizationWithRbac | null>(
    {
      queryKey: KEYS.all(),
      queryFn: getOrgs,
      select: (orgs) => {
        const matches = orgs.filter(
          (org) =>
            org.name.toUpperCase().startsWith("AON3D") &&
            (org.name.startsWith("AON3D Manufacturing") ||
              org.name.startsWith("AON3D Factory"))
        );
        if (matches.length > 1) {
          throw new Error(
            "Multiple factory orgs found. Rename the wrong one(s)."
          );
        }
        return matches[0] ?? null;
      },
    }
  );
}

/**
 * Hook to retrieve the list of organizations the current user is a member of.
 */
export function useMemberOrgs() {
  const config: UseQueryOptions<OrganizationWithRbac[]> = {
    queryKey: KEYS.all(),
    queryFn: getOrgs,
    select: (orgs) => orgs.filter((org) => org.isMember),
  };

  return useQuery(config);
}

/**
 * Hook to check if the user should have access to admin tools for any of the
 * orgs visible to them.
 */
export function useCheckAdminAccess() {
  const { data: orgs } = useOrgs();

  if (!orgs) return "loading";

  const hasAdminOrgs = orgs?.some((org) => {
    return org.permissions.includes(permissions.views.manage);
  });

  return hasAdminOrgs ? "yes" : "no";
}

/**
 * Retrieve the currently active organization for the user session.
 */
async function currentOrg(): Promise<OrganizationWithRbac | null> {
  const response = await api.get<ApiResponse<OrganizationWithRbac | null>>(
    "/organizations/current"
  );
  return response.data.data;
}

/**
 * Query hook for [[`currentOrg`]].
 *
 * This hook is for the initial retrieval of the current org, allowing
 * for the `useCurrentOrg` hook to be used for subsequent queries with
 * a guaranteed defined value.
 */
export function useCheckCurrentOrg() {
  const { data: user } = useCheckUser();

  const config: UseQueryOptions<OrganizationWithRbac | null> = {
    queryKey: KEYS.current(),
    queryFn: currentOrg,
    enabled: !!user,
  };

  return useQuery(config);
}

/**
 * Query hook for [[`currentOrg`]].
 */
export function useCurrentOrg() {
  const config: UseQueryOptions<OrganizationWithRbac | null> = {
    queryKey: KEYS.current(),
    queryFn: currentOrg,
  };

  const response = useQuery(config);

  const data = response.data;

  if (!data) {
    throw new Error("Current org data is required but was not provided.");
  }

  return { ...response, data };
}

/**
 * Mutation to set the currently active org for the user session.
 *
 * Setting the current org is always followed by a redirect, because the current
 * URL might be invalid in the new org, so we must navigate to a known-valid
 * page in that org.
 *
 * Therefore, use of this hook directly is discouraged. Use
 * `useSetLocationAndOrg` instead.
 */
export function useSetCurrentOrgInternal() {
  const queryClient = useQueryClient();

  async function setCurrentOrg({
    id,
  }: {
    id: Uuid;
    onError?: (err: { statusCode?: number; message?: string }) => void;
    onSuccess?: (org: Org) => void;
  }): Promise<Org> {
    // The organization must be one of the organizations the current user is a
    // member of, otherwise a 403 response will be returned.
    const response = await api.post<ApiResponse<Org>>(
      "/organizations/current",
      {
        id,
      }
    );
    return response.data.data;
  }

  return useMutation({
    mutationFn: setCurrentOrg,
    onSuccess: async (org: Org, { onSuccess }) => {
      const currentKey = KEYS.current();
      const orgKey = KEYS.org(org.id);

      // Cancel any in-flight fetches for the seeded keys so a stale read can't
      // clobber the switched-to org, then seed both from the server response.
      await queryClient.cancelQueries({ queryKey: currentKey });
      await queryClient.cancelQueries({ queryKey: orgKey });
      queryClient.setQueryData(currentKey, org);
      queryClient.setQueryData(orgKey, org);

      // When switching orgs, invalidate all other data from the old org,
      // excluding the two org records we just seeded.
      const seededHashes = [hashKey(currentKey), hashKey(orgKey)];
      void queryClient.invalidateQueries({
        predicate: (query) => !seededHashes.includes(query.queryHash),
      });

      onSuccess?.(org);
    },
    onError: (e: AxiosError<{ message?: string }>, { onError }) => {
      const responseData = e.response?.data;
      onError?.({
        statusCode: e.response?.status,
        message: responseData?.message,
      });
    },
  });
}

/**
 * Create a new org.
 */
async function createOrg(params: {
  name: string;
  makeCurrentUserMember: boolean;
  internal: boolean;
}): Promise<Org> {
  const response = await api.post<ApiResponse<Org>>(`/organizations`, params);
  return response.data.data;
}

/**
 * Mutation for [['createOrg']].
 */
export function useCreateOrg() {
  const queryClient = useQueryClient();

  const config = {
    mutationFn: createOrg,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
    },
  };

  return useMutation(config);
}

/**
 * Update information for the specified org.
 */
async function updateOrg(params: {
  id: Uuid;
  update: OrgUpdate;
}): Promise<Org> {
  const response = await api.patch<ApiResponse<Org>>(
    `/organizations/${params.id}`,
    params.update
  );
  return response.data.data;
}

/**
 * Mutation for [['updateOrg']].
 */
export function useUpdateOrg() {
  const queryClient = useQueryClient();

  const config = {
    mutationFn: updateOrg,
    onSuccess: (updatedOrg: Org) => {
      queryClient.setQueryData(KEYS.org(updatedOrg.id), updatedOrg);

      queryClient.setQueryData<Org[]>(KEYS.all(), (oldOrgs) =>
        oldOrgs
          ? oldOrgs.map((org) => (org.id === updatedOrg.id ? updatedOrg : org))
          : [updatedOrg]
      );
    },
  };

  return useMutation(config);
}

/**
 * Get the members of a specific organization by ID.
 */
async function getOrgUsers({
  orgId,
}: {
  orgId: Uuid;
}): Promise<OrganizationUser[]> {
  const response = await api.get<ApiResponse<OrganizationUser[]>>(
    `/organizations/${orgId}/users`
  );

  return response.data.data;
}

/**
 * Query hook for [[`getOrgUsers`]].
 */
export function useOrgUsers(orgId: Uuid) {
  return useQuery({
    queryKey: KEYS.orgUsers(orgId),
    queryFn: () => getOrgUsers({ orgId }),
  });
}

/**
 * Aggregated statistics for an organization fetched from separate endpoints.
 * Fields are `undefined` while their respective queries are still loading.
 * Extend this type as new per-org statistics are added.
 */
export type OrgStats = {
  userCount: number | undefined;
};

export type OrgWithStats = OrganizationWithRbac & OrgStats;

/**
 * Returns the list of all organizations enriched with per-org statistics.
 * Each stat field is `undefined` until its query resolves.
 */
export function useOrgsWithStats() {
  const result = useOrgs();
  const orgs = result.data ?? [];

  const usersResult = useQueries({
    queries: orgs.map((org) => ({
      queryKey: KEYS.orgUsers(org.id),
      queryFn: () => getOrgUsers({ orgId: org.id }),
    })),
  });

  // `useQueries` returns the query results directly, so aggregate properties.
  const isLoading = result.isLoading || usersResult.some((r) => r.isLoading);
  const isFetching = result.isFetching || usersResult.some((r) => r.isFetching);
  const isError = result.isError || usersResult.some((r) => r.isError);
  const error = result.error ?? usersResult.find((r) => r.error)?.error ?? null;
  const isSuccess = !isLoading && !isError;

  const data: OrgWithStats[] = orgs.map((org, i) => ({
    ...org,
    userCount: usersResult[i]?.data?.length,
  }));

  return { data, isLoading, isFetching, isError, error, isSuccess };
}

/**
 * Returns the current org's permissions, or an empty array if no org is selected.
 * Safe to call before orgs are loaded (won't throw).
 */
export function useCurrentOrgPermissions(): Permission[] {
  const { data: orgs } = useOrgs();
  const { data: currOrg } = useCheckCurrentOrg();
  return orgs?.find((o) => o.id === currOrg?.id)?.permissions ?? [];
}

/**
 * Returns true if the current user's only role in the current org is docs_user.
 */
export function useIsDocsOnly(): boolean {
  const { data: currOrg } = useCheckCurrentOrg();
  const roles = currOrg?.roles ?? [];
  return roles.length > 0 && roles.every((r) => r === "docs_user");
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  const removeMember = async (params: { orgId: Uuid; userId: Uuid }) => {
    await api.delete<ApiResponse<void>>(
      `/organizations/${params.orgId}/users/${params.userId}`
    );
  };

  const config = {
    mutationFn: removeMember,
    onSuccess: (_data: void, params: { orgId: Uuid; userId: Uuid }) => {
      void queryClient.invalidateQueries({
        queryKey: KEYS.orgUsers(params.orgId),
      });
    },
  };

  return useMutation(config);
}
